import { useState, useRef, useEffect } from 'react';

/**
 * OptimizedImage - Lazy loading image component with blur placeholder
 * Improves page load performance by deferring image loading until visible
 */
const OptimizedImage = ({ 
  src, 
  alt = '', 
  className = '', 
  priority = false,
  objectFit = 'cover',
  width,
  height,
  onLoad,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  // Placeholder blur style
  const placeholderStyle = {
    backgroundColor: '#1a1a1a',
    filter: isLoaded ? 'none' : 'blur(10px)',
    transition: 'filter 0.3s ease-out',
  };

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ 
        width: width || '100%', 
        height: height || '100%',
        ...placeholderStyle
      }}
    >
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          className={`w-full h-full transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectFit }}
          {...props}
        />
      )}
      
      {/* Loading skeleton */}
      {!isLoaded && isInView && (
        <div 
          className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] animate-pulse"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default OptimizedImage;
